import { normalizePaymentError } from "@/shared/services/payments/paymentErrors";
import { readApiCode, readApiStatus } from "@/shared/utils/apiError";
import { isTwoFactorCodeError, isTwoFactorEnrollmentError } from "@/shared/utils/twoFactorErrors";

export type BatchErrorKind =
  | "two_factor_code"
  | "two_factor_required"
  | "verification_required"
  | "self_approval"
  | "conflict"
  | "bad_request"
  | "not_found"
  | "access_denied"
  | "validation"
  | "network"
  | "unknown";

export interface BatchError {
  kind: BatchErrorKind;
  /** The backend `code` when it sent one (`fingerprint_mismatch`, `insufficient_funds`, …). */
  code: string | null;
  status: number | null;
  /** The backend's own localized sentence, joined; empty when the request never got an answer. */
  message: string;
}

/**
 * What a batch endpoint error can be classified as, in the order that matters: the two step-up refusals first
 * (they keep the person on the same screen and never latch an access denial), then the verification gate,
 * then the coded 409/400 refusals every money-moving action answers with, then the generic buckets
 * `normalizePaymentError` already knows.
 */
export function classifyBatchError(error: unknown): BatchError {
  const status = readApiStatus(error);
  const code = readApiCode(error);
  const normalized = normalizePaymentError(error);

  if (isTwoFactorCodeError(error)) {
    return { kind: "two_factor_code", code, status, message: normalized.message };
  }
  if (isTwoFactorEnrollmentError(error)) {
    return { kind: "two_factor_required", code, status, message: normalized.message };
  }
  // Dual control: a 403 that is not a missing permission, so it must never read as access_denied.
  if (code === "self_approval_forbidden") {
    return { kind: "self_approval", code, status, message: normalized.message };
  }
  if (normalized.kind === "verification_required") {
    return { kind: "verification_required", code, status, message: normalized.message };
  }
  if (status === 409) return { kind: "conflict", code, status, message: normalized.message };
  if (status === 404) return { kind: "not_found", code, status, message: normalized.message };
  if (normalized.kind === "access_denied")
    return { kind: "access_denied", code, status, message: normalized.message };
  if (normalized.kind === "validation")
    return { kind: "validation", code, status, message: normalized.message };
  if (status === 400 && code) return { kind: "bad_request", code, status, message: normalized.message };
  if (normalized.kind === "network") return { kind: "network", code, status, message: normalized.message };
  return { kind: "unknown", code, status, message: normalized.message };
}

/** Whether the failed request may have reached the server — the caller then re-reads instead of retrying. */
export const isUnconfirmedOutcome = (error: BatchError): boolean => error.kind === "network";
